/** The help panel: how to drive the deck, and every shortcut. */

import { type Component, For, Show } from 'solid-js';
import { SHORTCUTS } from '../../app/shortcuts.ts';
import { BUILD, commitUrl, releaseUrl } from '../../lib/version.ts';
import { account } from '../../state/library.ts';
import { LegalLinks } from '../legal/Links.tsx';
import { controlStyles } from './Controls.tsx';

/** What to say about YouTube depends on how far setup has got. */
function youtubeStatus(): string {
  const a = account();
  if (a.signedIn) return 'You are signed in. Your playlists and liked music are in the library.';
  if (a.configured) {
    return 'Tap the pill in the top-left to sign in with Google and load your playlists.';
  }
  return 'Demo mode is running with six generated records. To play your own YouTube Music library you need a free Google API client — see SETUP.md, then tap the top-left pill and choose “Use my own keys”.';
}

/**
 * Which build you are looking at.
 *
 * Both halves are links because both are questions someone asks next: the
 * release is what shipped, the commit is what is actually running.
 */
const BuildLine: Component = () => (
  <p class={controlStyles.build} data-testid="build-version">
    <a href={releaseUrl()} target="_blank" rel="noreferrer">
      {BUILD.version}
    </a>{' '}
    ·{' '}
    <a href={commitUrl()} target="_blank" rel="noreferrer">
      <code>{BUILD.commit}</code>
    </a>
    <Show when={BUILD.dirty}> · uncommitted</Show>
  </p>
);

export const Help: Component = () => (
  <>
    <p>
      Drag anywhere on the deck to orbit it. Pinch or scroll to zoom. Drag the{' '}
      <strong>record</strong> to scratch, drag the <strong>tonearm</strong> to seek or carry it back
      to its rest to stop, and drag the <strong>pitch fader</strong> to change speed. The{' '}
      <strong>start/stop</strong> button, <strong>33/45</strong> buttons and <strong>power</strong>{' '}
      button all work.
    </p>

    <h3>Keyboard</h3>
    <div class={controlStyles.keys}>
      <For each={SHORTCUTS}>
        {(shortcut) => (
          <div class={controlStyles.keyrow} data-testid="key-row">
            <span>{shortcut.description}</span>
            <kbd>{shortcut.keys}</kbd>
          </div>
        )}
      </For>
    </div>

    <h3>YouTube Music</h3>
    <p>{youtubeStatus()}</p>

    <h3>About</h3>
    <p>
      Built with Three.js. All textures, covers and demo music are generated in the browser — there
      are no bundled media files.
    </p>
    <BuildLine />

    <h3>Legal</h3>
    <LegalLinks />
  </>
);
